import { useState } from 'react';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { InsightsViewSelector } from './InsightsViewSelector';
import { ENTITY_VIEW_OPTIONS, type EntityView } from './insightsViewOptions';
import { IntensityTreemapPanel } from './IntensityTreemapPanel';
import { useGetIntensityMetricsQuery, useGetAssetsQuery } from '../../api/emissions';
import { useAppSelector } from '@reducers/index';
import { getCurrentInitiativeTree } from '@selectors/initiative';

interface IntensityDashboardProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
}

export const IntensityDashboard = ({ initiativeId, periodStart, periodEnd, includeChildren }: IntensityDashboardProps) => {
  const [view, setView] = useState<EntityView>('assets');

  return (
    <div className='tw:space-y-6'>
      <OrgIntensityView initiativeId={initiativeId} periodStart={periodStart} periodEnd={periodEnd} includeChildren={includeChildren} entityView={view} />

      <InsightsViewSelector
        label='Intensity for'
        options={ENTITY_VIEW_OPTIONS}
        value={view}
        onChange={setView}

      />

      {view === 'assets' && (
        <IntensityTreemapPanel
          initiativeId={initiativeId}
          periodStart={periodStart}
          periodEnd={periodEnd}
          includeChildren={includeChildren}
          entityView='assets'
        />
      )}
      {view === 'reporting-levels' && (
        <IntensityTreemapPanel
          initiativeId={initiativeId}
          periodStart={periodStart}
          periodEnd={periodEnd}
          includeChildren={includeChildren}
          entityView='reporting-levels'
        />
      )}
    </div>
  );
};

// --- Org-Level Intensity as MetricCards ---

const formatIntensity = (value: number | null | undefined): string => {
  if (value == null) return '—';
  if (value >= 1000) return `${(value / 1000).toFixed(1)}`;
  return value.toFixed(1);
};

const getUnit = (value: number | null | undefined, kgUnit: string, tUnit: string): string => {
  if (value == null) return kgUnit;
  return value >= 1000 ? tUnit : kgUnit;
};

const OrgIntensityView = ({ initiativeId, periodStart, periodEnd, includeChildren, entityView }: { initiativeId: string; periodStart: string; periodEnd: string; includeChildren?: boolean; entityView: EntityView }) => {
  const { data: metrics, isLoading } = useGetIntensityMetricsQuery(
    { initiativeId, periodStart, periodEnd, includeChildren },
    { skip: !initiativeId }
  );
  const { data: assetsResponse } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren }, { skip: !initiativeId });
  const allChildren = useAppSelector(getCurrentInitiativeTree);

  const entityCount = entityView === 'assets'
    ? (assetsResponse?.data?.length ?? 0)
    : allChildren.length;
  const entityLabel = entityView === 'assets' ? 'Assets' : 'Reporting Levels';
  const entityIcon = entityView === 'assets' ? 'fa-building' : 'fa-sitemap';

  return (
    <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-4'>
      <MetricCard
        label='Per Employee'
        value={formatIntensity(metrics?.perEmployee)}
        unit={getUnit(metrics?.perEmployee, 'kgCO₂e/employee', 'tCO₂e/employee')}
        subtext={metrics?.businessMetrics?.employeeCount ? `${metrics.businessMetrics.employeeCount.toLocaleString()} ${metrics.businessMetrics.employeeCountType ?? 'employees'}` : 'No employee data'}
        icon={<i className='fa fa-users tw:text-violet-600' />}
        iconBg='tw:bg-violet-100'
        borderColor='violet-500'
        isLoading={isLoading}
      />
      <MetricCard
        label='Per Revenue'
        value={formatIntensity(metrics?.perRevenue)}
        unit={getUnit(metrics?.perRevenue, 'kgCO₂e/$M', 'tCO₂e/$M')}
        subtext={metrics?.businessMetrics?.revenue ? `$${(metrics.businessMetrics.revenue / 1_000_000).toFixed(1)}M revenue` : 'No revenue data'}
        icon={<i className='fa fa-dollar-sign tw:text-emerald-600' />}
        iconBg='tw:bg-emerald-100'
        borderColor='emerald-500'
        isLoading={isLoading}
      />
      <MetricCard
        label='Per Floor Area'
        value={formatIntensity(metrics?.perFloorArea)}
        unit={getUnit(metrics?.perFloorArea, 'kgCO₂e/m²', 'tCO₂e/m²')}
        subtext={metrics?.businessMetrics?.floorArea ? `${metrics.businessMetrics.floorArea.toLocaleString()} m²` : 'No floor area data'}
        icon={<i className='fa fa-ruler-combined tw:text-sky-600' />}
        iconBg='tw:bg-sky-100'
        borderColor='sky-500'
        isLoading={isLoading}
      />
      <MetricCard
        label={entityLabel}
        value={String(entityCount)}
        icon={<i className={`fa ${entityIcon} tw:text-blue-600`} />}
        iconBg='tw:bg-blue-500/10'
        borderColor='blue-500'
      />
    </div>
  );
};
